import { DATE_FORMAT, formatShortISO, parse } from '@eneco/common/date';
import { ApiError } from '@eneco/dc/client/ApiError';
import { getProductTypeCombinations } from '@eneco/dc/services';
import { MachineConfig } from '@eneco/flows/types';
import {
  RequestModels_Products_Offers_OfferProductsPublicRequest,
  ResponseModels_Products_ProductCombinationResponseModel as ProductCombinationResponse,
} from '@monorepo-types/dc';
import { assign, setup, fromPromise } from 'xstate';

import { FlowContextSme } from '../context';
import { getEnergyTypesFromProductTypes } from '../helpers';
import { EnergyType } from '../types';

interface EnergyChoiceMachineContext {
  availableProducts?: Array<EnergyType>;
  errorMessage?: string;
  lastCallData?: {
    cachedResponse?: { data?: ProductCombinationResponse; values?: FlowContextSme };
    eventValues?: FlowContextSme;
  };
}

interface EnergyChoiceMachineEvent {
  type: 'IDLE' | 'GET_PRODUCT_TYPE_COMBINATIONS' | 'NOT_FOUND' | 'NOT_SUPPORTED';
  config: MachineConfig;
  values: FlowContextSme;
}

const hasUnsupportedProducts = (data?: ProductCombinationResponse['data']): boolean =>
  !!data?.commodity?.some(productCombination => !!productCombination?.includes('warmthProducts'));

const getService = (
  config: MachineConfig,
  values: FlowContextSme,
): Promise<{ data?: ProductCombinationResponse; values?: FlowContextSme }> =>
  new Promise((resolve, reject) => {
    const requestBody: RequestModels_Products_Offers_OfferProductsPublicRequest = {
      data: {
        postalCode: values.postalCode?.replace(/\s/g, ''),
        houseNumber: values.houseNumber ?? 0,
        houseNumberSuffix: values.houseNumberSuffix,
        gasValue: 0,
        electricityRedeliveryHigh: 0,
        electricityRedeliveryLow: 0,
        electricityRedeliveryTotal: 0,
        isMkb: !!values.isSME,
        offerType: 'NewEnergyContract',
        voucherCode: values.voucherCode,
        contractStartDate: values.deliveryDate && formatShortISO(parse(values.deliveryDate, DATE_FORMAT)),
        urgent: !!values?.isUrgent,
        ci: values?.ci ?? !!values?.isMoving,
        movingCustomer: !!values?.isMoving,
        includeRentalProducts: false,
        allowDynamicOrHybridOnly: false,
      },
    };

    return getProductTypeCombinations({
      businessUnit: config.businessUnit,
      label: config.label,
      requestBody,
    })
      .then(response => {
        if (!response?.data?.commodity?.length) {
          reject(new Error('Not Found'));
        }

        resolve({ data: response, values });
      })
      .catch((error: ApiError) => {
        reject(new Error(error?.message));
      });
  });

const actors = {
  get: fromPromise(
    async ({ input }: { input: { context: EnergyChoiceMachineContext; event: EnergyChoiceMachineEvent } }) => {
      const { context, event } = input;
      const { config, values } = event;
      const { lastCallData } = context;

      const flowContextKeys: Array<keyof FlowContextSme> = ['postalCode', 'houseNumber', 'houseNumberSuffix'];
      const shouldFetch = !flowContextKeys.every(key => values[key] === lastCallData?.eventValues?.[key]);

      if (shouldFetch) {
        return await getService(config, values);
      } else {
        return { data: lastCallData?.cachedResponse?.data, values: lastCallData?.eventValues };
      }
    },
  ),
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const energyChoiceMachine: any = setup({
  types: {} as {
    context: EnergyChoiceMachineContext;
    events: EnergyChoiceMachineEvent;
  },
  actors,
}).createMachine({
  id: 'energyChoice',
  initial: 'IDLE',
  context: {},
  on: {
    GET_PRODUCT_TYPE_COMBINATIONS: { target: '.FETCHING' },
    IDLE: { target: '.IDLE' },
  },
  states: {
    IDLE: {},
    FETCHING: {
      invoke: {
        src: 'get',
        input: ({ context, event }) => ({ context, event }),
        onDone: [
          {
            target: 'ERROR_NOT_SUPPORTED',
            guard: ({ event }) => {
              return hasUnsupportedProducts(event?.output?.data?.data);
            },
            actions: [
              assign(({ context, event }) => ({
                ...context,
                availableProducts: getEnergyTypesFromProductTypes(event?.output?.data?.data?.commodity || []),
                lastCallData: {
                  eventValues: event.output?.values,
                  cachedResponse: event.output,
                },
              })),
            ],
          },
          {
            target: 'SUCCESS',
            actions: [
              assign(({ context, event }) => ({
                ...context,
                availableProducts: getEnergyTypesFromProductTypes(event?.output?.data?.data?.commodity || []),
                lastCallData: {
                  eventValues: event.output?.values,
                  cachedResponse: event.output,
                },
              })),
            ],
          },
        ],
        onError: [
          {
            target: 'ERROR_NOT_FOUND',
            guard: ({ event }) => (event as { error?: Error })?.error?.message === 'Not Found',
            actions: [
              assign(({ context, event }) => ({
                ...context,
                errorMessage: (event as { error?: Error })?.error?.message,
              })),
            ],
          },
          {
            target: 'ERROR',
            actions: [
              assign(({ context, event }) => ({
                ...context,
                errorMessage: (event as { error?: Error })?.error?.message,
              })),
            ],
          },
        ],
      },
    },
    SUCCESS: {
      entry: [
        assign(({ context }) => ({
          ...context,
          errorMessage: undefined,
        })),
      ],
      after: {
        500: { target: 'IDLE' },
      },
    },
    ERROR: {},
    ERROR_NOT_SUPPORTED: {},
    ERROR_NOT_FOUND: {},
  },
});
